import { logger } from './logger';

type AnalyticsPrimitive = string | number | boolean | null;
export type AnalyticsProperties = Record<string, AnalyticsPrimitive | undefined>;

export type ProductAnalyticsEvent =
  | 'dashboard_loaded'
  | 'dashboard_hidden'
  | 'tab_viewed'
  | 'tab_duration'
  | 'section_viewed'
  | 'section_duration'
  | 'week_selected'
  | 'week_dropdown_opened'
  | 'spend_view_mode_changed'
  | 'external_link_clicked';

type AnalyticsPayload = {
  event: ProductAnalyticsEvent;
  properties: Record<string, AnalyticsPrimitive>;
  timestamp: number;
};

declare global {
  interface Window {
    gtag?: (command: 'event', eventName: string, params?: Record<string, unknown>) => void;
    plausible?: (eventName: string, options?: { props?: Record<string, unknown> }) => void;
    posthog?: {
      capture: (eventName: string, properties?: Record<string, unknown>) => void;
    };
  }
}

const SESSION_KEY = 'marlo:analyticsSessionId';
const SENSITIVE_KEY_PATTERN = /(email|name|note|content|token|secret|password|url|href|embed|creator|user_id|userid)/i;
const EMAIL_VALUE_PATTERN = /\S+@\S+\.\S+/;

let initialized = false;
let sessionStartedAt = Date.now();

function getAnalyticsEndpoint(): string | undefined {
  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  return typeof endpoint === 'string' && endpoint.trim().length > 0 ? endpoint.trim() : undefined;
}

function analyticsDisabled(): boolean {
  return import.meta.env.VITE_DISABLE_PRODUCT_ANALYTICS === 'true';
}

function hasVendorSink(): boolean {
  return typeof window.gtag === 'function' || typeof window.plausible === 'function' || typeof window.posthog?.capture === 'function';
}

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;

    const next = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return 'session-unavailable';
  }
}

function getDeviceType(): 'mobile' | 'desktop' {
  return window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
}

function sanitizeProperties(properties: AnalyticsProperties): Record<string, AnalyticsPrimitive> {
  const sanitized: Record<string, AnalyticsPrimitive> = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) continue;
    if (SENSITIVE_KEY_PATTERN.test(key) && key !== 'host' && key !== 'link_type') continue;
    if (typeof value === 'string' && (EMAIL_VALUE_PATTERN.test(value) || value.startsWith('http'))) continue;
    sanitized[key] = value;
  }

  return sanitized;
}

function getBaseProperties(): Record<string, AnalyticsPrimitive> {
  return {
    app: 'weekly-dashboard',
    path: window.location.pathname,
    device: getDeviceType(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    session_id: getSessionId(),
  };
}

function sendToEndpoint(payload: AnalyticsPayload): void {
  const endpoint = getAnalyticsEndpoint();
  if (!endpoint) return;

  const body = JSON.stringify(payload);

  try {
    if (navigator.sendBeacon) {
      const sent = navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
      if (sent) return;
    }

    fetch(endpoint, {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {
      // Analytics should never break the dashboard.
    });
  } catch {
    // Analytics should never break the dashboard.
  }
}

function sendToVendor(payload: AnalyticsPayload): void {
  const { event, properties } = payload;

  try {
    window.gtag?.('event', event, {
      event_category: 'dashboard_interaction',
      ...properties,
    });
    window.plausible?.(event, { props: properties });
    window.posthog?.capture(event, properties);
  } catch {
    // Vendor scripts are optional and should never break the dashboard.
  }
}

function classifyHost(hostname: string): string {
  if (hostname.includes('docs.google.com')) return 'google_docs';
  if (hostname.includes('instagram.com')) return 'instagram';
  if (hostname.includes('tiktok.com')) return 'tiktok';
  if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) return 'youtube';
  if (hostname.includes('twitter.com') || hostname.includes('x.com')) return 'x_twitter';
  if (hostname.includes('facebook.com')) return 'facebook';
  if (hostname.includes('linkedin.com')) return 'linkedin';
  return 'external';
}

function getSectionFromElement(element: Element | null): string | undefined {
  const section = element?.closest<HTMLElement>('[data-section]');
  return section?.dataset.section;
}

export function trackProductEvent(event: ProductAnalyticsEvent, properties: AnalyticsProperties = {}): void {
  if (analyticsDisabled()) return;

  const payload: AnalyticsPayload = {
    event,
    properties: sanitizeProperties({
      ...getBaseProperties(),
      ...properties,
    }),
    timestamp: Date.now(),
  };

  const hasConfiguredSink = Boolean(getAnalyticsEndpoint()) || hasVendorSink();

  if (import.meta.env.DEV) {
    logger.debug('Product analytics event', payload, 'ProductAnalytics');
  }

  if (!hasConfiguredSink) return;

  sendToVendor(payload);
  sendToEndpoint(payload);
}

export function initProductAnalytics(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  sessionStartedAt = Date.now();

  trackProductEvent('dashboard_loaded');

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest<HTMLAnchorElement>('a[href]');
      if (!anchor) return;

      const parsed = new URL(anchor.href, window.location.origin);
      if (parsed.origin === window.location.origin) return;

      trackProductEvent('external_link_clicked', {
        section: getSectionFromElement(anchor),
        host: parsed.hostname.replace(/^www\./, ''),
        link_type: classifyHost(parsed.hostname),
        target: anchor.target === '_blank' ? 'new_tab' : 'same_tab',
      });
    },
    { capture: true }
  );

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackProductEvent('dashboard_hidden', {
        duration_ms: Date.now() - sessionStartedAt,
      });
    }
  });
}
